import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import AssociatedProjectDisplay from '../application-core/AssociatedProjectDisplay';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import { ENTITLEMENTS } from '../application-core/constants';
import ActionText from '../core/ActionText';
import AddButton from '../core/AddButton';
import BasicLanguageFieldDisplay from '../core/BasicLanguageFieldDisplay';
import DatatypeDisplay from '../core/DatatypeDisplay';
import Errored from '../core/Errored';
import Notification from '../core/Notification';
import { ProjectConsumer } from '../core/providers/ProjectProvider';
import { globalStyles as styles } from '../core/style/gstheme';
import TitledPanel from '../core/TitledPanel';
import { MAX_DESCRIPTION_LENGTH, STATUS_ACCEPTED, STATUS_REJECTED } from '../core/utils/constants';
import { checkResourceEntitlements, getArrayValues, isInContextComponent, makeCancelable } from '../core/utils/utils';
import Warning from '../core/Warning';
import withGS from '../core/withGShoc';
import WorkflowResourceManager from '../core/workflow/WorkflowResourceManager';
import GS from '../grafsync/grafsync';
import { EXPANDED_NOTE_FIELDS, MAX_DEFINITION_LENGTH } from '../vocabulary/constants';
import TypeDisplay from '../vocabulary/taxonomies/TypeDisplay';
import { getPairwiseValues } from '../vocabulary/utils/utils';
import AdditionalDetail from './AdditionalDetail';
import { MAX_CLASSNAME_LENGTH } from './constants';
import DocumentSource from './DocumentSource';
import FindNamedResourceDialog from './FindNamedResourceDialog';
import IndividualTypeDisplay from './IndividualTypeDisplay';
import ResourceDropRelationshipsDisplay from './ResourceDropRelationshipsDisplay';
import Visibility from './Visibility';

class NamedIndividualDetail extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            warning: "",
            isErrored: undefined
        };
        this.cancellable = null;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    loadData(iri, options) {
        this.cancellable = makeCancelable(GS.loadData([iri], options, this.props.graph));
        return this.cancellable;
    }

    addNewLiteral = (property, propertyGraphIRI) => (language, text) => {
        GS.addProperty(this.props.id, this.props.graph, property, text, language, null, propertyGraphIRI);
    };

    addNewTypedLiteral = (property, propertyGraphIRI) => (value, datatype = null) => {
        GS.addProperty(this.props.id, this.props.graph, property, value, null, datatype, propertyGraphIRI);
    };

    addNewResource = (property, propertyGraphIRI, suppliedPropertyType) => (iri) => {
        GS.loadData([iri], {}, this.props.graph).then(() => GS.addProperty(this.props.id, this.props.graph, property, iri, null, null, propertyGraphIRI, suppliedPropertyType));
    };

    addDefinedBy = () => GS.addProperty(this.props.id, this.props.graph, "isDefinedBy", this.props.ontologyIRI);

    onUpdate = (property, lang, index, propertyGraphIRI) => (value) => {
        this.props.onUpdate(property, value, lang, index, propertyGraphIRI);
    };

    onDelete = (property, lang, datatype, propertyGraphIRI) => (value) => this.props.onDelete(property, value, lang, datatype, propertyGraphIRI);

    onAddClass = async (value) => this.props.onAdd("type", value);

    includeExistingResource = (property, propertyGraphIRI) => async (newIRI) => {
        const { graph, id } = this.props;
        // We load the resource we are about to use to make sure rendering works okay
        GS.loadData([newIRI], {}, graph, true).then(() => {
            GS.addProperty(id, graph, property, newIRI, null, null, propertyGraphIRI);
        });
    };

    onExitWarning = () => this.setState({ warning: "" });

    doPath(payload) {
        this.cancellable = makeCancelable(GS.path(payload));
        return this.cancellable;
    }

    checkIndividual = async (id, property) => {
        if (this.props.id === id) return "You cannot add a resource to itself.";
        if (this.props.data.differentFrom && this.props.data.differentFrom.includes(id)) {
            return "You cannot add an individual that is already different from.";
        }
        if (this.props.data.sameAs && this.props.data.sameAs.includes(id)) {
            return "You cannot add an individual that is already same as.";
        }
        GS.handle("load-ni");
        await this.loadData(id).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (!GS.isLoaded(id, this.props.graph)) return null;
        const niData = GS.data(id, this.props.graph);
        if (property === "differentFrom") {
            if (niData.sameAs && niData.sameAs.some(s => s === this.props.id)) {
                return "You cannot add an individual as different from that is already same as to this individual.";
            }
        }
        else {
            if (niData.differentFrom && niData.differentFrom.some(d => d === this.props.id)) {
                return "You cannot add an individual as same as that is already different from this individual.";
            }
        }
        return null;
    };

    moveIndividual = (property) => (id) => {
        if (this.props.data[property] && this.props.data[property].includes(id)) return;
        this.checkIndividual(id, property)
            .then(result => {
                if (result === null) this.addNewResource(property)(id);
                else {
                    if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ warning: result });
                }
            })
            .catch((e) => {
                if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
            });
    };

    render() {

        const { data, classes, defaultLang, project, ontologyIRI, graph, viewLang, showOntologyProjects, associatedProject,
            availableAnnotationProperty, hiddenProps, buildLink, id, propertyTemplates, projectUsers, useWorkflow, entitlements } = this.props;
        const { warning, isErrored } = this.state;

        if (isErrored) return <Errored dataTest="ni-error" data={isErrored} />;
        if (data._notAvailable) return null;
        if (warning !== "") return <Warning message={warning} onExitWarning={this.onExitWarning} />;
        if (data.pendingReplacement) return <ActionText>This named individual has been replaced. Select again to view new data.</ActionText>;

        const isReadOnly = checkResourceEntitlements(data, ENTITLEMENTS.EDIT_ONTOLOGY)
            || (data.deprecated === true)
            || (useWorkflow && "status" in data && (data.status[0] === STATUS_ACCEPTED || data.status[0] === STATUS_REJECTED));

        const hidden = hiddenProps ?
            hiddenProps.map(prop => prop.substring(36))
            :
            [];
        const visibleNoteFields = EXPANDED_NOTE_FIELDS.filter(field => !hidden.includes(field.property));
        // Get array values is fine as we are dealing with IRIs so sorting not an issue
        const dataTypeList = getArrayValues(data, "type");
        const isDefinedBy = getArrayValues(data, "isDefinedBy");
        // Some of the classes may be aliased so convert to IRIs
        let resourceClasses = dataTypeList.map(rc => GS.getIRIfromAlias(rc) || rc);

        return (
            <div>
                <ProjectConsumer>
                    {({ projectLang, hasComponent, ontologyProjectAssociation }) => (
                        <form autoComplete="off" className={classes.selection}>
                            {data.pendingDeletion &&
                                <Notification error={true} text="This named individual is pending deletion." dataTest="pending-deletion" />
                            }
                            {dataTypeList.includes("OwlClass") &&
                                <Notification text="This named individual is also a class." dataTest="punning" />
                            }
                            {data.deprecated &&
                                <div>
                                    <Notification text="This named individual is deprecated." dataTest="deprecated" />
                                </div>
                            }
                            <Grid container spacing={1}>
                                {useWorkflow &&
                                    <Grid item xs={12}>
                                        <WorkflowResourceManager
                                            disabled={!isInContextComponent(data, ontologyIRI) || data.deprecated || data.pendingDeletion}
                                            status={data.status}
                                            id={id}
                                            key={id}
                                            resourceClass="http://www.w3.org/2002/07/owl#NamedIndividual"
                                            componentIRI={ontologyIRI}
                                            entitlements={entitlements}
                                            defaultLang={defaultLang}
                                            assignedUser={data.assignedUser}
                                            assignedNote={data.assignedNote}
                                            rejectedNote={data.rejectedNote}
                                            projectUsers={projectUsers}
                                            project={project}
                                            graph={graph} />
                                    </Grid>
                                }
                                <Grid item md={12} lg={6}>
                                    <BasicLanguageFieldDisplay
                                        dataTest="resource-label"
                                        message={"Add a Label"} defaultLang={defaultLang}
                                        title="Label" property="label" values={data.label}
                                        name="Label"
                                        placeholder="Enter label text"
                                        projectLang={projectLang}
                                        graph={graph}
                                        onePerLang={true}
                                        maxLength={MAX_CLASSNAME_LENGTH}
                                        onDelete={this.onDelete}
                                        onUpdate={this.onUpdate} isReadOnly={isReadOnly}
                                        onAddLanguage={this.addNewLiteral} />
                                </Grid>
                                <Grid item md={12} lg={6}>
                                    <TitledPanel dataTest="isDefinedBy" height="full" title="Is Defined By"
                                        actions={!isReadOnly && isDefinedBy.length === 0 ?
                                            <AddButton onClick={this.addDefinedBy} dataTest="add-isdefined-by" title="Make defined by this ontology" />
                                            :
                                            null
                                        }>
                                        {isDefinedBy.map(idb =>
                                            <CommonIriDisplay
                                                id={idb}
                                                key={idb}
                                                components={hasComponent}
                                                projectIRI={project}
                                                handleNotAvailable={true}
                                                property="isDefinedBy"
                                                isReadOnly={true}
                                                compact={true}
                                                graph={graph}
                                                source={id}
                                                wrap={false}
                                                viewLang={viewLang}
                                                defaultLang={defaultLang} />
                                        )}
                                    </TitledPanel>
                                </Grid>
                                <Grid item md={12} lg={6}>
                                    <IndividualTypeDisplay
                                        graph={graph}
                                        isReadOnly={isReadOnly}
                                        classIRIs={GS.getClassIRIs(id, graph)}
                                        defaultLang={defaultLang}
                                        components={hasComponent}
                                        sourceComponent={ontologyIRI}
                                        viewLang={viewLang}
                                        source={id}
                                        onAddClass={this.onAddClass}
                                        projectIRI={project}
                                        id={id}
                                        values={data.type} />
                                </Grid>
                                {ontologyProjectAssociation && ontologyProjectAssociation.length > 0 &&
                                    <Grid item md={12} lg={6}>
                                        <TypeDisplay
                                            offerInheritance={false}
                                            graph={graph}
                                            title="Classes (Other Projects)"
                                            nonLocalOnly={true}
                                            classIRIs={GS.getClassIRIs(id, graph)}
                                            ontologyProjectAssociation={ontologyProjectAssociation}
                                            isReadOnly={isReadOnly}
                                            defaultLang={defaultLang}
                                            viewLang={viewLang}
                                            onAddClass={this.onAddClass}
                                            id={id}
                                            values={data.type} />
                                    </Grid>
                                }
                                {
                                    [
                                        { rel: 'differentFrom', title: "Different From" },
                                        { rel: 'sameAs', title: "Same As" }

                                    ].map((item, index) => {
                                        const itemData = getArrayValues(data, item.rel);
                                        const ebp = [];
                                        if (item.rel === "differentFrom") {
                                            ebp.push({
                                                predicate: [
                                                    "http://www.w3.org/2002/07/owl#sameAs"
                                                ],
                                                target: data.id,
                                                pathOperators: [""]
                                            });
                                        }
                                        if (item.rel === "sameAs") {
                                            ebp.push({
                                                predicate: [
                                                    "http://www.w3.org/2002/07/owl#differentFrom"
                                                ],
                                                target: data.id,
                                                pathOperators: [""]
                                            });
                                        }
                                        let exclude = [];
                                        if (data.differentFrom) exclude = exclude.concat(data.differentFrom);
                                        if (data.sameAs) exclude = exclude.concat(data.sameAs);
                                        return <Grid item md={12} lg={6} key={"rel-" + index}>
                                            <ResourceDropRelationshipsDisplay
                                                dataTest={item.rel}
                                                project={project}
                                                property={item.rel}
                                                graph={graph}
                                                excludeByPath={ebp}
                                                onMove={this.moveIndividual(item.rel)}
                                                addNewResource={this.addNewResource(item.rel)}
                                                title={item.title}
                                                useWorkflow={useWorkflow}
                                                view="namedIndividual"
                                                permitExternal={true}
                                                permittedExternalType="NamedIndividual"
                                                components={hasComponent}
                                                exclude={exclude}
                                                searchClass="http://www.w3.org/2002/07/owl#NamedIndividual"
                                                buildLink={buildLink}
                                                source={data.id}
                                                defaultLang={defaultLang}
                                                viewLang={viewLang}
                                                ontologyIRI={ontologyIRI}
                                                values={itemData}
                                                isReadOnly={isReadOnly} />
                                        </Grid>;
                                    })
                                }
                                {!hiddenProps.includes("http://www.w3.org/2000/01/rdf-schema#comment") &&
                                    <Grid item md={12} lg={6}>
                                        <BasicLanguageFieldDisplay
                                            dataTest="comments"
                                            message={"Add a Comment"}
                                            defaultLang={viewLang || defaultLang}
                                            title="Comments"
                                            graph={graph}
                                            placeholder="Enter comment text"
                                            property="comment"
                                            values={data.comment}
                                            projectLang={projectLang}
                                            multiline={true}
                                            maxLength={MAX_DESCRIPTION_LENGTH}
                                            onDelete={this.onDelete}
                                            onUpdate={this.onUpdate}
                                            isReadOnly={isReadOnly}
                                            onAddLanguage={this.addNewLiteral} />
                                    </Grid>
                                }
                                {!hiddenProps.includes("http://www.w3.org/2000/01/rdf-schema#seeAlso") &&
                                    [
                                        {
                                            rel: 'seeAlso',
                                            title: "See Also"
                                        }
                                    ].map((item, index) => {
                                        const itemData = getArrayValues(data, item.rel, false);
                                        return <Grid item md={12} lg={6} key={"rel-" + index}>
                                            <DatatypeDisplay
                                                title={item.title}
                                                source={data.id}
                                                datatype={""}
                                                components={hasComponent}
                                                defaultLang={defaultLang}
                                                viewLang={viewLang}
                                                useWorkflow={useWorkflow}
                                                projectLang={projectLang}
                                                property={item.rel}
                                                values={itemData}
                                                onDelete={this.onDelete}
                                                context={ontologyIRI}
                                                onUpdate={this.onUpdate}
                                                projectIRI={project}
                                                noRangeOptions={{
                                                    language: false,
                                                    dateTime: false,
                                                    datatype: true,
                                                    boolean: false,
                                                    excludeDatatypes: ["INTEGER", "NNINTEGER", "DECIMAL", "LONG", "FLOAT", "DATE", "DOUBLE"]
                                                }}
                                                graph={graph}
                                                associatedProjectGraph={graph}
                                                isReadOnly={isReadOnly}
                                                canAddValues={true}
                                                addNewResource={this.addNewResource(item.rel, graph, "@id")}
                                                onAddLiteral={this.addNewLiteral(item.rel)}
                                                onAddTypedLiteral={this.addNewTypedLiteral(item.rel)}
                                            />
                                        </Grid>;
                                    })
                                }
                                <Grid item md={12} lg={6}>
                                    <Visibility
                                        graph={graph}
                                        viewLang={viewLang}
                                        componentIRI={ontologyIRI}
                                        source={id}
                                        sourceType={data.type}
                                        defaultLang={defaultLang}
                                        sources={data.visibility} />
                                </Grid>
                                <Grid item md={12} lg={6}>
                                    <DocumentSource
                                        graph={graph}
                                        componentIRI={ontologyIRI}
                                        viewLang={viewLang}
                                        defaultLang={defaultLang}
                                        sources={data.documentSource} />
                                </Grid>
                            </Grid>
                            {visibleNoteFields.length > 0 &&
                                <React.Fragment>
                                    <Typography
                                        data-test="annotation-props-title"
                                        className={classes.subMainHeading}
                                        style={{ marginTop: '20px' }}>SKOS Annotations</Typography>
                                    <Grid data-test="additional-annotations" container spacing={1} classes={
                                        {
                                            "spacing-xs-2": this.props.classes.fullGrid
                                        }
                                    }>
                                        {EXPANDED_NOTE_FIELDS.filter(field => !hidden.includes(field.property)).map(field => {
                                            let existingValues = undefined;
                                            if (field.pairwise) {
                                                existingValues = getPairwiseValues(data);
                                            }
                                            return <Grid key={field.title} item md={12} lg={6}>
                                                <BasicLanguageFieldDisplay
                                                    dataTest={field.property}
                                                    message={"Add " + field.message}
                                                    defaultLang={viewLang || defaultLang}
                                                    title={field.title}
                                                    required={false}
                                                    onePerLang={field.onePerLang === true}
                                                    property={field.property}
                                                    values={data[field.property]}
                                                    existingValues={existingValues}
                                                    maxLength={field.maxLength || MAX_DEFINITION_LENGTH}
                                                    projectLang={projectLang}
                                                    placeholder={field.placeholder}
                                                    name={field.message}
                                                    onDelete={this.onDelete}
                                                    onUpdate={this.onUpdate}
                                                    isReadOnly={isReadOnly}
                                                    multiline={field.multiline}
                                                    onAddLanguage={this.addNewLiteral} />
                                            </Grid>;
                                        }
                                        )}
                                    </Grid>
                                </React.Fragment>
                            }
                            {availableAnnotationProperty && availableAnnotationProperty.length > 0 &&
                                <React.Fragment>
                                    <Typography
                                        data-test="annotation-props-title"
                                        className={classes.subMainHeading}
                                        style={{ marginTop: '20px' }}>Additional Annotations</Typography>
                                    <Grid data-test="additional-annotations" container spacing={1} classes={
                                        {
                                            "spacing-xs-2": this.props.classes.fullGrid
                                        }
                                    }>
                                        <AdditionalDetail
                                            data={data}
                                            projectIRI={project}
                                            viewLang={viewLang}
                                            defaultLang={defaultLang}
                                            projectLang={projectLang}
                                            graph={graph}
                                            useWorkflow={useWorkflow}
                                            ontologyIRI={ontologyIRI}
                                            isReadOnly={isReadOnly}
                                            onDelete={this.onDelete}
                                            context={ontologyIRI}
                                            onUpdate={this.onUpdate}
                                            addNewTypedLiteral={this.addNewTypedLiteral}
                                            addNewLiteral={this.addNewLiteral}
                                            addNewResource={this.addNewResource}
                                            availableAnnotationProperty={availableAnnotationProperty} />
                                    </Grid>
                                </React.Fragment>
                            }
                            {showOntologyProjects && ontologyProjectAssociation &&
                                <Grid container spacing={1}>
                                    {
                                        [...ontologyProjectAssociation].sort().map((opa) =>
                                            <AssociatedProjectDisplay
                                                resourceData={data}
                                                reify={false}
                                                dialogComponent={FindNamedResourceDialog}
                                                onReify={this.onShowReification}
                                                graph={graph}
                                                projectIRI={project}
                                                schemeIRI={ontologyIRI}
                                                statements={data.isSubjectOfStatement}
                                                useWorkflow={false}
                                                propertyTemplates={propertyTemplates}
                                                isReadOnly={isReadOnly}
                                                viewLang={viewLang}
                                                projectLang={projectLang}
                                                projectType="OntologyProject"
                                                linkedData={[]}
                                                associatedTaxonomyProject={associatedProject}
                                                addNewLiteral={this.addNewLiteral}
                                                addNewTypedLiteral={this.addNewTypedLiteral}
                                                includeExistingResource={this.includeExistingResource}
                                                components={hasComponent}
                                                resourceClasses={resourceClasses}
                                                onDelete={this.onDelete}
                                                onUpdate={this.onUpdate}
                                                key={opa}
                                                id={opa}
                                                defaultLang={defaultLang} />
                                        )}
                                </Grid>
                            }
                        </form>
                    )}
                </ProjectConsumer>
                <div data-test="info-panel-bottom" >&#160;</div>
            </div>
        );
    }
}

NamedIndividualDetail.defaultProps = {
    hiddenProps: []
};

NamedIndividualDetail.propTypes = {
    classes: PropTypes.object,
    hiddenProps: PropTypes.array,
    id: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    showOntologyProjects: PropTypes.bool.isRequired,
    propertyTemplates: PropTypes.array,
    graph: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    viewLang: PropTypes.string,
    associatedProject: PropTypes.array,
    buildLink: PropTypes.func.isRequired,
    project: PropTypes.string.isRequired,
    availableAnnotationProperty: PropTypes.array,
    ontologyIRI: PropTypes.string.isRequired,
    onDelete: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired
};

export default withStyles(styles)(withGS(NamedIndividualDetail, ["ALL"], null, {}, true));